/**
 * A score as a MusicXML 4.0 partwise document.
 *
 * Partwise rather than timewise because every program that reads MusicXML reads partwise,
 * and Verovio -- the renderer -- wants it. Nothing about layout is written: no page or
 * system geometry, no default positions, no fonts. A renderer lays the music out from the
 * notes regardless, and carrying a previous engraver's coordinates is what makes converted
 * files an order of magnitude larger than the music in them.
 */

import { restRunsByStart } from "../document/multirest";
import type { Score } from "../document/types";
import { writeXml } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { el } from "../xml/write";
import { divisionsFor, partNames } from "./common";
import { headerNodes, partListNode } from "./export-header";
import type { HeaderOptions } from "./export-header";
import { measureNode } from "./export-measure";
import type { ExportContext } from "./export-measure";
import { staffMap } from "./export-ossia";
import { planInstruments } from "./export-instruments";
import { planPart } from "./export-plan";

export interface ExportOptions extends HeaderOptions {
  readonly indent?: string;
  /**
   * Write the document's own ids into the file as `id` attributes.
   *
   * Off by default: a file the user saves should carry the music and not this editor's
   * bookkeeping. The renderer turns it on, because Verovio copies the attribute onto the SVG
   * element it draws and that is what makes a click on a note findable again.
   */
  readonly ids?: boolean;
  /**
   * Work out a beam for every note whose beaming was left to the engraver.
   *
   * Off by default, so that what is saved says what the document says. The renderer turns it
   * on: MusicXML has no way to write "group this by the metre", so a file that does not
   * spell its beams out is drawn with none. See `export-beams.ts`.
   */
  readonly beams?: boolean;
}

const PROLOGUE = [
  '<?xml version="1.0" encoding="UTF-8"?>',
  '<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN"' +
    ' "http://www.musicxml.org/dtds/partwise.dtd">',
];

/** The document as a tree, for a caller that wants to inspect or wrap it. */
export function toMusicXmlTree(score: Score, options: ExportOptions = {}): XmlNode {
  const divisions = divisionsFor(score);
  const names = partNames(score);

  const parts = score.parts.map((part, index) => {
    const partId = names.get(part.id) ?? `P${index + 1}`;
    // A part gathers its silent bars and a score does not, which is the score's own
    // setting rather than a choice made here. See `document/multirest.ts`.
    const restRuns = score.layout.multiRests
      ? restRunsByStart(score, part)
      : new Map<number, number>();
    const context: ExportContext = {
      score,
      part,
      plan: planPart(score, part, options.beams === true),
      divisions,
      staves: staffMap(part),
      leading: index === 0,
      ids: options.ids === true,
      partId,
      restRuns,
      instruments: planInstruments(score, part, partId),
    };
    const measures: (XmlNode | null)[] = [];
    for (let at = 0; at < score.measures.length; at += 1) measures.push(measureNode(context, at));
    return el("part", { id: partId }, measures);
  });

  return el("score-partwise", { version: "4.0" }, [
    ...headerNodes(score, options),
    partListNode(score, names),
    ...parts,
  ]);
}

/** The document as text, ready to hand to a renderer or write to a file. */
export function toMusicXml(score: Score, options: ExportOptions = {}): string {
  return writeXml(toMusicXmlTree(score, options), {
    prologue: PROLOGUE,
    indent: options.indent ?? "  ",
  });
}
